import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';

import { SubscriptionPlan } from '@vi-iot/client';

import { BookingDashboardDevice, BookingStore, InstallationsStore, Platform } from '@store';

@Component({
  selector: 'app-booking-devices',
  templateUrl: './booking-devices.component.html',
  styleUrls: ['./booking-devices.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingDevicesComponent implements OnInit, OnDestroy {
  readonly MAX_SELECTED_DEVICES = 10;
  readonly platform = Platform;
  readonly BookingPlans = SubscriptionPlan;

  @Input() invoiceNumber: string;
  @Output() nextPage = new EventEmitter<void>();

  selectedPlan: SubscriptionPlan;
  devices: BookingDashboardDevice[] | null;
  isCheckout = false;
  maxSelectedDevices = this.MAX_SELECTED_DEVICES;
  selectedInstallationsCount: number;
  isMaxSelectedDevicesReached = false;

  searchTerm: string = null;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public bookingStore: BookingStore,
    protected installationsStore: InstallationsStore,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.bookingStore.isBookingDevicesInInitState$
      .pipe(
        first((val) => val),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(() => {
        this.bookingStore.loadBookingInstallation();
      });
    this.bookingStore.selectedPlan$.pipe(first()).subscribe((plan) => {
      this.selectedPlan = plan;
    });

    this.bookingStore.selectedInstallationsCount$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedInstallationsCount) => {
        this.selectedInstallationsCount = selectedInstallationsCount;

        this.isMaxSelectedDevicesReached =
          this.selectedInstallationsCount >= this.maxSelectedDevices;
      });

    if (this.selectedPlan === SubscriptionPlan.Trial) {
      this.bookingStore.trialSubscriptions$.pipe(first()).subscribe((trialSubscriptions) => {
        this.maxSelectedDevices = trialSubscriptions?.remaining || this.MAX_SELECTED_DEVICES;
      });
    }

    if (this.invoiceNumber) {
      this.bookingStore
        .getAmountOfAvailableVouchers(this.invoiceNumber)
        .pipe(first())
        .subscribe((amountOfAvailableVouchers) => {
          this.maxSelectedDevices = amountOfAvailableVouchers;
        });
    }

    this.bookingStore.getBookingInstallations$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((devices) => {
        this.devices = devices;
      });
  }

  isSelectedInstallation(installation: BookingDashboardDevice): Observable<boolean> {
    return this.bookingStore.isSelectedInstallation(installation.vin);
  }

  isDisabledInstallation(installation: BookingDashboardDevice): Observable<boolean> {
    return this.bookingStore
      .isSelectedInstallation(installation.vin)
      .pipe(
        map(
          (isSelected) =>
            (this.isMaxSelectedDevicesReached && !isSelected) ||
            !installation.canBeBooked ||
            (!installation.canBeBundled && Boolean(this.invoiceNumber)) ||
            installation.plan === SubscriptionPlan.Plus
        )
      );
  }

  onInstallationSelect(installation: BookingDashboardDevice): void {
    this.bookingStore.toggleInstallationSelection(installation.vin);
  }

  onPageChange(paginator: PageEvent): void {
    const { pageIndex } = paginator;
    const nextPage = pageIndex > -1 ? pageIndex : 0;
    this.bookingStore.setBookingPaginationPage(nextPage);
    window.scrollTo(0, 0);
  }

  search(text?: string): void {
    if (this.searchTerm === text) {
      return;
    }
    this.searchTerm = text;
    this.bookingStore.setBookingPaginationPage(0);
    this.bookingStore.setSearchInputValue(text);
  }

  onContinue(): void {
    this.clearQueryParams();
    this.nextPage.emit();

    if (this.selectedPlan !== SubscriptionPlan.Trial) {
      this.bookingStore.getSubscriptionsDetails();
    }
  }

  onContinueBookingContingentSubscription(): void {
    this.nextPage.emit();
    this.bookingStore.getSubscriptionsDetails();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.bookingStore.setBookingPaginationPage(0);
    this.bookingStore.setSearchInputValue(null);
  }

  private clearQueryParams(): void {
    const params = { ...this.activatedRoute.snapshot.queryParams };
    delete params.selectedStep;
    delete params.boilerSerial;
    delete params.update;

    this.router.navigate([], { queryParams: params });
  }
}
